import { action } from "typesafe-actions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import { IActionItemDynamicForm } from "./actionItemDynamicForm.types";

export const FETCH_ACTION_ITEM_DYNAMIC_FORM_LIST_PROGRESS =
  "FETCH_ACTION_ITEM_DYNAMIC_FORM_LIST_PROGRESS";
export const FETCH_ACTION_ITEM_DYNAMIC_FORM_LIST_SUCCESS =
  "FETCH_ACTION_ITEM_DYNAMIC_FORM_LIST_SUCCESS";
export const FETCH_ACTION_ITEM_DYNAMIC_FORM_LIST_FAILED =
  "FETCH_ACTION_ITEM_DYNAMIC_FORM_LIST_FAILED";

export const fetchActionItemDynamicFormListProgress = () =>
  action(FETCH_ACTION_ITEM_DYNAMIC_FORM_LIST_PROGRESS);
export const fetchActionItemDynamicFormListSuccess = (
  data: IActionItemDynamicForm[],
  totalRecords: number,
) => action(FETCH_ACTION_ITEM_DYNAMIC_FORM_LIST_SUCCESS, { data, totalRecords });
export const fetchActionItemDynamicFormListFailed = () =>
  action(FETCH_ACTION_ITEM_DYNAMIC_FORM_LIST_FAILED);

export const fetchActionItemDynamicFormListAsync =
  (
    companyOutletUuid: string,
    pageNumber: number,
    itemPerPage: number,
    pageLimit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    actionItemCategoryUuid?: string,
    actionItemDynamicFormUuid?: string,
    onCallBack?: (isSuccess: boolean, data: IActionItemDynamicForm[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchActionItemDynamicFormListProgress());
    let callbackData: { isSuccess: boolean; data: IActionItemDynamicForm[] } =
      {
        isSuccess: false,
        data: [],
      };

    try {
      dispatch(saveLoaderProgress());
      let searchQuery = "";

      if (actionItemDynamicFormUuid) {
        searchQuery += `&action_item_dynamic_form_uuid=${actionItemDynamicFormUuid}`;
      }

      if (actionItemCategoryUuid) {
        searchQuery += `&action_item_category_uuid=${actionItemCategoryUuid}`;
      }

      if (status && status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      if (searchValue?.length ?? 0) {
        const advanceFilter = [...(searchValue || [])];
        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      const response = await api.get(
        `/leads/get-action-item-dynamic-form?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${itemPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}` +
          searchQuery,
      );

      const data: IActionItemDynamicForm[] = response.data.data || [];
      const totalRecords = response.data.totalRecords || 0;

      callbackData = { isSuccess: true, data };
      dispatch(fetchActionItemDynamicFormListSuccess(data, totalRecords));
    } catch (error: any) {
      callbackData = { isSuccess: false, data: [] };
      dispatch(fetchActionItemDynamicFormListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            error?.response?.data?.message ||
            "Failed to fetch action item dynamic forms",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }

    onCallBack?.(callbackData.isSuccess, callbackData.data);
  };

export const upsertActionItemDynamicFormAsync =
  (
    data: Partial<IActionItemDynamicForm>,
    onCallback?: (isSuccess: boolean, data?: IActionItemDynamicForm) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      if (!data.company_outlet_uuid) {
        throw new Error("company_outlet_uuid is required");
      }

      const response = await api.post(
        "/leads/upsert-action-item-dynamic-form",
        data,
      );
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: response.data.message || "Form saved successfully",
          displayAs: "snackbar",
        }),
      );
      onCallback?.(true, response.data.data);
      return response.data;
    } catch (error: any) {
      dispatch(saveLoaderCompleted());
      onCallback?.(false);
      dispatch(
        showMessage({
          type: "error",
          message: error?.response?.data?.message || "Failed to save form",
          displayAs: "snackbar",
        }),
      );
      throw error;
    }
  };

export const CLEAR_ACTION_ITEM_DYNAMIC_FORM_STATE =
  "CLEAR_ACTION_ITEM_DYNAMIC_FORM_STATE";
export const clearActionItemDynamicFormState = () =>
  action(CLEAR_ACTION_ITEM_DYNAMIC_FORM_STATE);
